/**
 * サイドバーのアクティブ表示の URL 同期（data 属性駆動）
 *
 * マークアップ規約:
 *   <a data-lms-nav-item href="/admin/users">ユーザー一覧</a>
 *   <a data-lms-nav-item class="lms-sb-row lms-sb-row--section" href="/ch010_x/001_a">…</a>
 *
 * 部分更新遷移（htmx）ではサイドバーが再描画されず、サーバーが付けた現在地表示
 * （is-active / aria-current / is-current-ancestor）が古いままになるため、遷移完了時に
 * 現在の URL パスと一致する行へ付け替える（design.md N5 / ADR-0011）。
 *
 * 対象はサーバー側が現在地表示を付けうるリンク（data-lms-nav-item 付き）で、
 * ホーム・ロール別メニュー項目・**カリキュラムのセクション**の 3 種である。
 * 2026-09-11 の highlight-current-nav-location より前は「セクションリンクは is-active の
 * 対象外のため data-lms-nav-item を付けないこと」という規約だったが、セクション行も
 * 現在地表示の対象になったため前提ごと更新した。サイドバーにリンクを足すときは、
 * 現在地表示をしたいものにだけこの属性を付けること。
 *
 * 祖先行（現在地のセクションを配下に含むチャプター行・サブチャプター行）は <button> で
 * href を持たないため URL から逆引きできない。現在地リンクから [data-lms-accordion] を
 * 外側へ辿り、その直下のトリガー行へ付ける（design.md D6）。
 *
 * アコーディオンの開閉状態（data-open）には触れない。開閉は利用者の操作結果をそのまま使う
 * （design.md D3 / partial-navigation spec「開閉状態を壊さない」）。祖先行のシェブロン強調は
 * 開閉状態によらず常時 CSS だけで行う（2026-09-11 ユーザー指示改訂。旧: 折りたたみ中だけ行末に
 * ドットを出す方式。accordion.ts はいずれの方式でも無変更）。
 *
 * 常設サイドバーとモバイルドロワーには同じツリーが 2 本描画される（layout/base.html の
 * 77 行と 101 行）。document 全体を走査するので両方が同時に更新される。
 */

/** 祖先行に付けるクラス。サイドバー以外では使っていない（layout.css と sidebar.html のみ） */
const ANCESTOR_CLASS = "is-current-ancestor";

/**
 * 祖先行として扱うトリガー行。カリキュラムのチャプター行・サブチャプター行だけを対象にする。
 * ロール別メニューの見出し（.lms-sb-row--admin-head）もアコーディオンのトリガーだが、
 * 現在地の「祖先」ではないので primary 文字に変えてはならない。
 */
const ANCESTOR_ROW_SELECTOR = ".lms-sb-row--chapter, .lms-sb-row--sub";

export function updateNavActive(): void {
  const currentPath = location.pathname;

  // 付与より先に全部外す。章をまたぐ遷移で、前の章の祖先クラスが残るのを防ぐ
  // （design.md Risks「部分更新で祖先クラスの外し漏れが起きる」）。
  document.querySelectorAll<HTMLElement>(`.${ANCESTOR_CLASS}`).forEach((row) => {
    row.classList.remove(ANCESTOR_CLASS);
  });

  document.querySelectorAll<HTMLAnchorElement>("a[data-lms-nav-item]").forEach((link) => {
    const linkPath = new URL(link.href, location.origin).pathname;
    const active = linkPath === currentPath;
    link.classList.toggle("is-active", active);
    if (active) {
      link.setAttribute("aria-current", "page");
      markCurrentAncestors(link);
    } else {
      link.removeAttribute("aria-current");
    }
  });
}

/** 現在地リンクから外側のアコーディオンを順に遡り、その直下のトリガー行へ祖先クラスを付ける */
function markCurrentAncestors(link: HTMLElement): void {
  let node: HTMLElement | null = link.parentElement;
  while (node) {
    const root = node.closest<HTMLElement>("[data-lms-accordion]");
    if (!root) return;
    const trigger = directTrigger(root);
    if (trigger && trigger.matches(ANCESTOR_ROW_SELECTOR)) {
      trigger.classList.add(ANCESTOR_CLASS);
    }
    // 1 つ外側のアコーディオンへ。closest は自分自身も対象にするので、親から探し直す
    node = root.parentElement;
  }
}

/** アコーディオンルートの直下にあるトリガー行を返す（孫のトリガーは対象外。accordion.ts と同じ流儀） */
function directTrigger(root: HTMLElement): HTMLElement | null {
  for (const child of Array.from(root.children)) {
    if (child instanceof HTMLElement && child.hasAttribute("data-lms-accordion-trigger")) {
      return child;
    }
  }
  return null;
}
